import { Box, Grid, TextField } from "@material-ui/core";
import React, { useEffect } from "react";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import { useStyles } from "./style";
import { useFormik } from "formik";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import CommonButton from "src/components/CommonButton/CommonButton";
import { object, string, number } from "yup";
import {
  amountDue,
  currentSelectedPaymentId,
  currentSelectedPaymentStatus,
  currentSelectedRegNo,
  setAmountDue,
} from "src/app-redux/commercial/commercialSlice";
import {
  addPaymentReceivedDrawer,
  getPaymentDetailsById,
} from "src/app-redux/commercial/actions/commercialActions";
import moment from "moment";
import { currencyUnitList } from "src/common/utils/app.dropdowns";
import { useDropzone } from "react-dropzone";
import { getAllFileCompressionConfigs } from "src/app-redux/configuration/actions/configurationActions";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";

export interface IDrawerAddPaymentProps {}

export default function DrawerAddPayment(props: IDrawerAddPaymentProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const selectedPaymentStatus = useAppSelector(currentSelectedPaymentStatus);

  //Redux
  const selectedPaymentId = useAppSelector(currentSelectedPaymentId);
  const selectedRegNo = useAppSelector(currentSelectedRegNo);
  const selectedDueAmount = useAppSelector(amountDue);
  const allCurrencies = currencyUnitList;
  const allModesOfPayment = [
    "Cash",
    "Easypaisa",
    "Jazz Cash",
    "Bank Transfer",
    "Cheque",
    "Adjustment",
  ]; //modesOfPayment;
  const [fileToUpload, setFileToUpload] = React.useState<any>();
  const [validFileTypes, setValidFileTypes] = React.useState<any>([]);
  const [validFileSize, setValidFileSize] = React.useState(0);
  const [isValidFileType, setIsValidFileType] = React.useState<boolean>(true);
  const [isValidFileSize, setIsValidFileSize] = React.useState<boolean>(true);

  let fileTypesArr: any[] = [];
  let maxFileSize = 0;

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    const split = acceptedFiles[0].name.split(".");

    setIsValidFileType(fileTypesArr.includes(`.${split[split.length - 1]}`.toLowerCase()));
    setIsValidFileSize(acceptedFiles[0].size / (1024 * 1024) <= maxFileSize);

    setFileToUpload(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  useEffect(() => {
    getFileConfig();
  }, []);

  const getFileConfig = async () => {
    const res = await dispatch(getAllFileCompressionConfigs());
    if (res.payload && res.payload[0]) {
      setValidFileTypes(res.payload[0].fileTypes.split(","));
      setValidFileSize(res.payload[0].fileSizeThreshold);
      fileTypesArr = res.payload[0].fileTypes.split(",");
      maxFileSize = res.payload[0].fileSizeThreshold;
    }
  };

  const validationSchema = object({
    amount: number()
      .required("This field is required")
      .min(1, "Minimum 1 is allowed.")
      .max(
        selectedDueAmount,
        selectedDueAmount > 0
          ? `Maximum ${selectedDueAmount} is allowed.`
          : "Payment already received"
      ),
    currency: string().required("This field is required"),
    paymentMethod: string().required("This field is required"),
    remarks: string().max(3000, "Maximum 3000 characters allowed"),
    chequeNo: string().when("paymentMethod", {
      is: "Cheque",
      then: string()
        .required("Cheque No is required")
        .max(100, "Cheque No cannot be greater than 100"),
      otherwise: string().notRequired(), // No validation when hidden
    }),
  });

  const formik = useFormik<any>({
    enableReinitialize: true,
    initialValues: {
      gatePassPaymentId: selectedPaymentId,
      registerationNumber: selectedRegNo,
      paymentDate: moment(new Date()).format("YYYY-MM-DDTHH:mm"),
      amount: "",
      vehicleId: "",
      currency: "PKR",
      paymentMethod: "",
      remarks: "",
      chequeNo: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any, actions) => {
      const res: any = await dispatch(
        addPaymentReceivedDrawer({ ...values, fileRecord: fileToUpload })
      );
      dispatch(getPaymentDetailsById(selectedPaymentId));
      if (res.payload) {
        dispatch(setAmountDue(selectedDueAmount - formik.values.amount));
        actions.setSubmitting(false);
        formik.resetForm();
        formik.setValues(formik.initialValues);
        setFileToUpload(null);
        setIsValidFileType(true);
        setIsValidFileSize(true);
        return;
      }
    },
  });

  return (
    <div className={classes.drawerDetails}>
      {/* Drawer Status Item  */}

      <Grid container spacing={2} className="topContainer" style={{ marginTop: "5px" }}>
        <Grid container direction="row" className="itemRow" spacing={2}>
          {/* Payment Date */}
          <Grid item xs={12} md={12} style={{ height: "55px" }}>
            <CommonTextFieldDate
              cssClassName="customTextField"
              label={"Date"}
              name={"paymentDate"}
              value={formik.values.paymentDate}
              required={true}
              onChange={(e: any) => {
                formik.setFieldValue("paymentDate", e);
              }}
              maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              error={formik.touched.paymentDate && Boolean(formik.errors.paymentDate)}
              helperText={formik.touched.paymentDate && formik.errors.paymentDate}
            />
          </Grid>

          {/* Amount */}
          <Grid item xs={12} md={12}>
            <Box>
              <TextField
                className="customTextField"
                type="number"
                required
                size="small"
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  inputProps: { min: 0, max: selectedDueAmount },
                }}
                label="Amount"
                name="amount"
                placeholder="Enter Amount"
                disabled={false}
                value={formik.values.amount}
                onChange={formik.handleChange}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
              />
            </Box>
            <Grid container direction="row" justifyContent="flex-end" style={{ marginTop: "5px" }}>
              <Box fontWeight={700}>Amount Pending:</Box>
              <Box marginLeft={"5px"}>
                PKR {selectedDueAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Box>
            </Grid>
          </Grid>
          {/* Currency */}
          <Grid item xs={12} md={12}>
            <Box>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                disabled={false}
                label="Currency"
                name="currency"
                optionsType={allCurrencies}
                getOptionLabel={(option) => option}
                required
                value={allCurrencies.find((st: any) => st === formik.values.currency) || null}
                onChange={(option: any) => {
                  option && option !== "" ? formik.setFieldValue("currency", option) : "";
                }}
                error={formik.touched.currency && Boolean(formik.errors.currency)}
                helperText={formik.touched.currency && formik.errors.currency}
              />
            </Box>
          </Grid>
          {/* Mode of Payment */}
          <Grid item xs={12} md={12}>
            <Box>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                label="Mode of Payment"
                placeholder="Mode 0f Payment"
                name="paymentMethod"
                optionsType={allModesOfPayment}
                required
                getOptionLabel={(option: any) => option || ""}
                value={formik.values.paymentMethod || null}
                onChange={(option: any) => {
                  option && option !== "" ? formik.setFieldValue("paymentMethod", option) : "";
                  if (option !== "Cheque") {
                    formik.setFieldValue("chequeNo", "");
                  }
                }}
                error={formik.touched.paymentMethod && Boolean(formik.errors.paymentMethod)}
                helperText={formik.touched.paymentMethod && formik.errors.paymentMethod}
              />
            </Box>
          </Grid>
          {formik.values.paymentMethod === "Cheque" && (
            <Grid item xs={12} md={12}>
              <Box>
                <TextField
                  className="customTextField"
                  type="string"
                  size="small"
                  required
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label="Cheque No"
                  name="chequeNo"
                  placeholder="Enter Your Cheque No"
                  disabled={false} // Enable field only when payment method is "Cheque"
                  value={formik.values.chequeNo}
                  onChange={formik.handleChange}
                  error={formik.touched.chequeNo && Boolean(formik.errors.chequeNo)}
                  helperText={formik.touched.chequeNo && formik.errors.chequeNo}
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={12}>
            <Box>
              <TextField
                className="customTextField"
                type="string"
                size="small"
                multiline
                rows={1}
                rowsMax={8}
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                label="Remarks"
                name="remarks"
                placeholder="Enter Your Remarks"
                disabled={false}
                value={formik.values.remarks}
                onChange={formik.handleChange}
                error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                helperText={formik.touched.remarks && formik.errors.remarks}
              />
            </Box>
          </Grid>
          {/* File Upload */}
          <Grid item xs={12} md={12}>
            <Box
              {...getRootProps()}
              p={0}
              pl={2}
              border={"1px solid #72CA00"}
              borderRadius={5}
              height={38}
              pb={4}
              overflow={"hidden"}
              borderColor={isDragActive ? "error.main" : "secondary.main"}
            >
              <input {...getInputProps()} />

              {fileToUpload ? (
                <p style={{ marginTop: 0 }}> {fileToUpload.name}</p>
              ) : isDragActive ? (
                <p>Drop a file here ...</p>
              ) : (
                <p style={{ color: "rgb(181 178 178)", marginTop: 9, paddingBottom: 10 }}>
                  {/* Drag and drop file here,or click to select a file */}
                  Upload File
                </p>
              )}
            </Box>
            <Box>
              <p style={{ color: "#f44336", marginBottom: 0 }}>
                <ul
                  style={{
                    paddingLeft: "16px",
                    margin: 0,
                    display: isValidFileType ? "none" : "block",
                  }}
                >
                  <li>Only {validFileTypes} files are allowed.</li>
                </ul>
                <ul
                  style={{
                    paddingLeft: "16px",
                    margin: 0,
                    display: isValidFileSize ? "none" : "block",
                  }}
                >
                  <li>File size cannot exceed {validFileSize}MB.</li>
                </ul>
              </p>
            </Box>
          </Grid>

          <Grid container item xs={12} md={12}>
            <Grid item xs={12} md={9}>
              <Box textAlign="left" mt={1}>
                <CommonButton
                  cssClass="customSubmitBtn"
                  onClick={() => {
                    formik.resetForm();
                    formik.setValues(formik.initialValues);
                    setFileToUpload(null);
                    setIsValidFileType(true);
                    setIsValidFileSize(true);
                  }}
                >
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  disable={!isValidFileType || !isValidFileSize || selectedPaymentStatus == "Void"}
                  onClick={() => {
                    formik.submitForm();
                  }}
                >
                  Save
                </CommonButton>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}
